import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Plus, Calendar, List, BarChart3, ChevronLeft, ChevronRight, GripVertical, Check, Trash2 } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';

const statusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Overdue'];
const phaseOptions = ['Lead', 'Consultation', 'Design', 'Execution', 'Handover'];
const priorityOptions = ['On Track', 'Urgent', 'Overdue'];
const zoomLevels = ['Week', 'Month', 'Quarter'];

const statusColors = {
  'Not Started': '#C67D5B',
  'In Progress': '#3A8C8C',
  'Completed': '#7DA68A',
  'On Hold': '#D4D0C8',
  'Overdue': '#C4727F'
};

const priorityDots = {
  'On Track': 'bg-[#7DA68A]',
  'Urgent': 'bg-[#C67D5B]',
  'Overdue': 'bg-[#C4727F]'
};

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

function formatDateShort(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]}`;
}

function getDaysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1);
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
}

export default function TimeframeModule({ projectId, projectStartDate, projectEndDate }) {
  const [confirm, confirmDialog] = useConfirm();
  const [timeframe, setTimeframe] = useState({ items: [], overall_progress: 0, status_counts: {} });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('chart'); // 'chart' or 'list'
  const [zoom, setZoom] = useState('Month');
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [chartStartDate, setChartStartDate] = useState(projectStartDate || new Date().toISOString().split('T')[0]);
  const [chartEndDate, setChartEndDate] = useState(projectEndDate || addDays(new Date(), 90));
  const chartRef = useRef(null);
  
  const [form, setForm] = useState({
    name: '', start_date: '', end_date: '', duration_days: 0,
    status: 'Not Started', progress: 0, assigned_to: '', assigned_name: '',
    phase: 'Design', notes: '', priority: 'On Track'
  });

  // Drag state for Gantt bar move/resize
  const [dragPreview, setDragPreview] = useState({}); // {itemId: {start_date, end_date}}
  const dragRef = useRef(null); // { itemId, mode, startX, origStart, origEnd, columnWidth }
  const justDraggedRef = useRef(false);
  const dateColumnsRef = useRef([]);

  const computeDayDelta = useCallback((clientX) => {
    const d = dragRef.current;
    if (!d) return 0;
    return Math.round((clientX - d.startX) / d.columnWidth);
  }, []);

  const loadTimeframe = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/timeframe`);
      setTimeframe(res.data);
      if (res.data.project_start_date) setChartStartDate(res.data.project_start_date);
      if (res.data.project_end_date) setChartEndDate(res.data.project_end_date);
    } catch { toast.error('Failed to load timeframe'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadTimeframe(); }, [loadTimeframe]);

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = computeDayDelta(e.clientX);
      if (delta === d.lastDelta) return;
      d.lastDelta = delta;
      let ns = d.origStart;
      let ne = d.origEnd;
      if (d.mode === 'move') {
        ns = addDays(d.origStart, delta);
        ne = addDays(d.origEnd, delta);
      } else if (d.mode === 'resize-left') {
        const candidate = addDays(d.origStart, delta);
        if (getDaysBetween(candidate, d.origEnd) >= 1) ns = candidate;
      } else if (d.mode === 'resize-right') {
        const candidate = addDays(d.origEnd, delta);
        if (getDaysBetween(d.origStart, candidate) >= 1) ne = candidate;
      }
      setDragPreview((prev) => ({ ...prev, [d.itemId]: { start_date: ns, end_date: ne } }));
    };
    const onUp = async () => {
      const d = dragRef.current;
      if (!d) return;
      const moved = (d.lastDelta || 0) !== 0;
      dragRef.current = null;
      document.body.style.cursor = '';
      if (moved) {
        justDraggedRef.current = true;
        setTimeout(() => { justDraggedRef.current = false; }, 200);
      }
      const final = dragPreview[d.itemId];
      setDragPreview((prev) => {
        const next = { ...prev };
        delete next[d.itemId];
        return next;
      });
      if (!final || (final.start_date === d.origStart && final.end_date === d.origEnd)) return;
      try {
        await api.put(`/projects/${projectId}/timeframe/${d.itemId}`, {
          start_date: final.start_date,
          end_date: final.end_date,
          duration_days: getDaysBetween(final.start_date, final.end_date),
        });
        toast.success(d.mode === 'move' ? 'Item moved' : 'Item resized');
        loadTimeframe();
      } catch {
        toast.error('Failed to update');
        loadTimeframe();
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [computeDayDelta, dragPreview, projectId, loadTimeframe]);

  const startDrag = (e, item, mode) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.start_date || !item.end_date) return;
    const columnWidth = (dateColumnsRef.current && dateColumnsRef.current[0]?.width) || 28;
    dragRef.current = {
      itemId: item.id,
      mode,
      startX: e.clientX,
      origStart: item.start_date.split('T')[0],
      origEnd: item.end_date.split('T')[0],
      columnWidth,
      lastDelta: 0,
    };
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize';
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setForm({
      name: '', start_date: new Date().toISOString().split('T')[0], end_date: '', duration_days: 7,
      status: 'Not Started', progress: 0, assigned_to: '', assigned_name: '',
      phase: 'Design', notes: '', priority: 'On Track'
    });
    setShowItemModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      start_date: item.start_date?.split('T')[0] || '',
      end_date: item.end_date?.split('T')[0] || '',
      duration_days: item.duration_days || 0,
      status: item.status || 'Not Started',
      progress: item.progress || 0,
      assigned_to: item.assigned_to || '',
      assigned_name: item.assigned_name || '',
      phase: item.phase || 'Design',
      notes: item.notes || '',
      priority: item.priority || 'On Track'
    });
    setShowItemModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate logic
      if (field === 'start_date' && prev.duration_days > 0) {
        updated.end_date = addDays(value, prev.duration_days - 1);
      } else if (field === 'end_date' && prev.start_date) {
        updated.duration_days = getDaysBetween(prev.start_date, value);
      } else if (field === 'duration_days' && prev.start_date) {
        updated.end_date = addDays(prev.start_date, value - 1);
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Item name is required');
      return;
    }
    try {
      if (editingItem) {
        await api.put(`/projects/${projectId}/timeframe/${editingItem.id}`, form);
        toast.success('Item updated');
      } else {
        await api.post(`/projects/${projectId}/timeframe`, form);
        toast.success('Item created');
      }
      setShowItemModal(false);
      loadTimeframe();
    } catch { toast.error('Failed to save item'); }
  };

  const handleDelete = async () => {
    if (!editingItem) return;
    if (!(await confirm({ title: 'Delete timeframe item?', message: `"${editingItem.title || editingItem.name || 'This item'}" will be removed from the schedule.`, confirmLabel: 'Delete' }))) return;
    try {
      await api.delete(`/projects/${projectId}/timeframe/${editingItem.id}`);
      toast.success('Item deleted');
      setShowItemModal(false);
      loadTimeframe();
    } catch { toast.error('Failed to delete item'); }
  };

  const updateItemStatus = async (itemId, newStatus) => {
    try {
      await api.put(`/projects/${projectId}/timeframe/${itemId}`, { status: newStatus });
      loadTimeframe();
    } catch { toast.error('Failed to update status'); }
  };

  // Generate date columns for Gantt chart
  const generateDateColumns = () => {
    const columns = [];
    const start = new Date(chartStartDate);
    const end = new Date(chartEndDate);
    
    let current = new Date(start);
    while (current <= end) {
      if (zoom === 'Week') {
        columns.push({
          date: new Date(current),
          label: formatDateShort(current),
          width: 40
        });
        current.setDate(current.getDate() + 1);
      } else if (zoom === 'Month') {
        columns.push({
          date: new Date(current),
          label: current.getDate() === 1 || columns.length === 0 ? formatDateShort(current) : current.getDate().toString(),
          width: 28,
          isFirstOfMonth: current.getDate() === 1
        });
        current.setDate(current.getDate() + 1);
      } else {
        // Quarter - show weeks
        columns.push({
          date: new Date(current),
          label: `W${Math.ceil(current.getDate() / 7)}`,
          width: 60
        });
        current.setDate(current.getDate() + 7);
      }
    }
    return columns;
  };

  const getBarPosition = (item, columns) => {
    if (!item.start_date || !columns.length) return { left: 0, width: 0 };
    
    const itemStart = new Date(item.start_date);
    const itemEnd = new Date(item.end_date || item.start_date);
    const chartStart = columns[0].date;
    
    const startOffset = Math.max(0, (itemStart - chartStart) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (itemEnd - itemStart) / (1000 * 60 * 60 * 24) + 1);
    
    const columnWidth = columns[0].width;
    const left = startOffset * columnWidth;
    const width = duration * columnWidth;
    
    return { left, width };
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const dateColumns = generateDateColumns();
  dateColumnsRef.current = dateColumns;
  const todayColumn = dateColumns.findIndex(col => isToday(col.date));

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="timeframe-module">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="section-heading text-sm font-semibold font-['Manrope']">Project Timeframe</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{timeframe.items.length} items · {timeframe.overall_progress}% complete</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-[#E8E6E1]/50 rounded-lg p-0.5">
            <button
              onClick={() => setView('chart')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'chart' ? 'bg-white shadow-sm text-[#2C2C2E]' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}
              data-testid="view-chart-btn"
            >
              <BarChart3 size={14} className="inline mr-1" /> Chart
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'list' ? 'bg-white shadow-sm text-[#2C2C2E]' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}
              data-testid="view-list-btn"
            >
              <List size={14} className="inline mr-1" /> List
            </button>
          </div>
          
          {/* Zoom Level */}
          <Select value={zoom} onValueChange={setZoom}>
            <SelectTrigger className="h-8 w-24 rounded-lg border-[#E8E6E1] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {zoomLevels.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
          
          <button onClick={openCreateModal} data-testid="add-timeframe-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
            <Plus size={14} /> Add Item
          </button>
        </div>
      </div>

      {/* Date Range Setup */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#6B7280]">Chart Start</Label>
            <Input type="date" value={chartStartDate} onChange={e => setChartStartDate(e.target.value)}
              className="h-8 w-36 rounded-lg border-[#D4D0C8] text-xs" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#6B7280]">Chart End</Label>
            <Input type="date" value={chartEndDate} onChange={e => setChartEndDate(e.target.value)}
              className="h-8 w-36 rounded-lg border-[#D4D0C8] text-xs" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#6B7280]">Duration</Label>
            <span className="text-sm font-medium text-[#2C2C2E]">{getDaysBetween(chartStartDate, chartEndDate)} days</span>
          </div>
          
          {/* Overall Progress Bar */}
          <div className="flex-1 flex items-center gap-3 ml-auto">
            <div className="flex-1 h-2 bg-[#E8E6E1] rounded-full overflow-hidden max-w-48">
              <div className="h-full bg-[#3A8C8C] rounded-full transition-all duration-500" style={{ width: `${timeframe.overall_progress}%` }} />
            </div>
            <span className="text-sm font-semibold text-[#3A8C8C]">{timeframe.overall_progress}%</span>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Completed', count: timeframe.status_counts.completed || 0, color: '#7DA68A', icon: '✅' },
          { label: 'In Progress', count: timeframe.status_counts.in_progress || 0, color: '#3A8C8C', icon: '🔵' },
          { label: 'Pending', count: timeframe.status_counts.pending || 0, color: '#C67D5B', icon: '🟠' },
          { label: 'Overdue', count: timeframe.status_counts.overdue || 0, color: '#C4727F', icon: '🔴' }
        ].map(stat => (
          <div key={stat.label} className="glass-card rounded-xl p-3 text-center">
            <div className="text-2xl font-semibold" style={{ color: stat.color }}>{stat.count}</div>
            <div className="text-[10px] text-[#6B7280] uppercase mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Chart View */}
      {view === 'chart' && (
        <div className="glass-card rounded-xl overflow-hidden" data-testid="gantt-chart">
          <div className="overflow-x-auto" ref={chartRef}>
            <div className="min-w-max">
              {/* Header Row */}
              <div className="flex border-b border-[#E8E6E1] bg-[#FAFAF8]">
                <div className="w-48 px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider border-r border-[#E8E6E1] shrink-0">
                  Item
                </div>
                <div className="flex relative">
                  {dateColumns.map((col, idx) => (
                    <div
                      key={idx}
                      className={`text-center py-2 text-[9px] text-[#6B7280] border-r border-[#E8E6E1]/50 ${col.isFirstOfMonth ? 'border-l border-[#D4D0C8]' : ''} ${isToday(col.date) ? 'bg-[#3A8C8C]/10' : ''}`}
                      style={{ width: col.width }}
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Item Rows */}
              {timeframe.items.map((item) => {
                const preview = dragPreview[item.id];
                const displayItem = preview ? { ...item, start_date: preview.start_date, end_date: preview.end_date } : item;
                const pos = getBarPosition(displayItem, dateColumns);
                const isDragging = !!preview;
                return (
                  <div key={item.id} className="flex border-b border-[#E8E6E1] hover:bg-[#FAFAF8]/50 transition-colors group" data-testid={`timeframe-row-${item.id}`}>
                    {/* Item Name */}
                    <div className="w-48 px-3 py-2.5 flex items-center gap-2 border-r border-[#E8E6E1] shrink-0">
                      <button className="opacity-0 group-hover:opacity-50 cursor-grab">
                        <GripVertical size={12} className="text-[#6B7280]" />
                      </button>
                      <button
                        onClick={() => updateItemStatus(item.id, item.status === 'Completed' ? 'In Progress' : 'Completed')}
                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${item.status === 'Completed' ? 'bg-[#7DA68A] border-[#7DA68A]' : 'border-[#D4D0C8] hover:border-[#3A8C8C]'}`}
                      >
                        {item.status === 'Completed' && <Check size={10} className="text-white" />}
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="text-xs text-[#2C2C2E] font-medium truncate hover:text-[#3A8C8C] transition-colors flex-1 text-left"
                      >
                        {item.name}
                      </button>
                    </div>
                    
                    {/* Bar Area */}
                    <div className="flex-1 relative h-10" style={{ width: dateColumns.reduce((sum, c) => sum + c.width, 0) }}>
                      {/* Today Line */}
                      {todayColumn >= 0 && (
                        <div
                          className="absolute top-0 bottom-0 w-px bg-[#2C2C2E] opacity-30 z-10"
                          style={{ left: todayColumn * dateColumns[0].width + dateColumns[0].width / 2, borderStyle: 'dashed' }}
                        />
                      )}
                      
                      {/* Bar */}
                      {pos.width > 0 && (
                        <div
                          onMouseDown={(e) => startDrag(e, item, 'move')}
                          onClick={() => { if (justDraggedRef.current) return; openEditModal(item); }}
                          className={`absolute top-1.5 h-7 rounded-md transition-shadow hover:shadow-md group/bar select-none ${isDragging ? 'cursor-grabbing shadow-lg ring-2 ring-[#3A8C8C]/40' : 'cursor-grab'}`}
                          style={{
                            left: pos.left,
                            width: Math.max(pos.width, 20),
                            backgroundColor: statusColors[item.status] || '#D4D0C8'
                          }}
                          data-testid={`gantt-bar-${item.id}`}
                        >
                          {/* Left Resize Handle */}
                          <div
                            onMouseDown={(e) => startDrag(e, item, 'resize-left')}
                            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize opacity-0 group-hover/bar:opacity-100 hover:bg-white/40 rounded-l-md transition-opacity"
                            data-testid={`gantt-resize-left-${item.id}`}
                            title="Resize start"
                          />
                          {/* Right Resize Handle */}
                          <div
                            onMouseDown={(e) => startDrag(e, item, 'resize-right')}
                            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize opacity-0 group-hover/bar:opacity-100 hover:bg-white/40 rounded-r-md transition-opacity"
                            data-testid={`gantt-resize-right-${item.id}`}
                            title="Resize end"
                          />

                          {/* Progress Fill */}
                          <div
                            className="absolute inset-0 rounded-md opacity-40 pointer-events-none"
                            style={{
                              width: `${item.progress}%`,
                              backgroundColor: 'rgba(0,0,0,0.2)'
                            }}
                          />
                          
                          {/* Bar Label */}
                          {pos.width > 60 && (
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-white font-medium truncate pr-2 pointer-events-none" style={{ maxWidth: pos.width - 8 }}>
                              {item.name}
                            </span>
                          )}
                          
                          {/* Progress % / Date hint while dragging */}
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-white/80 font-medium pointer-events-none">
                            {isDragging ? `${getDaysBetween(displayItem.start_date, displayItem.end_date)}d` : `${item.progress}%`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Empty State */}
              {timeframe.items.length === 0 && (
                <div className="py-12 text-center">
                  <Calendar size={32} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
                  <p className="text-sm text-[#6B7280]">No timeline items yet</p>
                  <button onClick={openCreateModal} className="text-xs text-[#3A8C8C] hover:underline mt-2">+ Add your first item</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="glass-card rounded-xl overflow-hidden" data-testid="timeframe-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAFAF8]">
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Item Name</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Start</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">End</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Days</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Progress</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Status</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#6B7280] uppercase border-b border-[#E8E6E1]">Assigned</th>
              </tr>
            </thead>
            <tbody>
              {timeframe.items.map(item => (
                <tr key={item.id} onClick={() => openEditModal(item)} className="hover:bg-[#FAFAF8]/50 cursor-pointer transition-colors" data-testid={`timeframe-list-row-${item.id}`}>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1]">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${priorityDots[item.priority] || 'bg-[#D4D0C8]'}`} />
                      <span className="font-medium text-[#2C2C2E]">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1] text-[#6B7280]">{formatDate(item.start_date)}</td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1] text-[#6B7280]">{formatDate(item.end_date)}</td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1] text-center text-[#6B7280]">{item.duration_days}</td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1]">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className="h-full bg-[#3A8C8C] rounded-full" style={{ width: `${item.progress}%` }} />
                      </div>
                      <span className="text-xs text-[#6B7280]">{item.progress}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1] text-center">
                    <Badge 
                      className="text-[9px]"
                      style={{ 
                        backgroundColor: `${statusColors[item.status]}20`,
                        color: statusColors[item.status]
                      }}
                    >
                      {item.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 border-b border-[#E8E6E1] text-[#6B7280]">{item.assigned_name || '-'}</td>
                </tr>
              ))}
              {timeframe.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-[#6B7280]">
                    No timeline items yet. <button onClick={openCreateModal} className="text-[#3A8C8C] hover:underline">Add one</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Item Detail Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-md backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{editingItem ? 'Edit Timeline Item' : 'Add Timeline Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Item Name *</Label>
              <Input value={form.name} onChange={e => handleFormChange('name', e.target.value)}
                placeholder="e.g., Design Development"
                className="mt-1 h-9 rounded-lg border-[#D4D0C8]" data-testid="timeframe-item-name" />
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => handleFormChange('start_date', e.target.value)}
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => handleFormChange('end_date', e.target.value)}
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Duration (Days)</Label>
                <Input type="number" value={form.duration_days} onChange={e => handleFormChange('duration_days', parseInt(e.target.value) || 0)}
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-xs" min={1} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Status</Label>
                <Select value={form.status} onValueChange={v => handleFormChange('status', v)}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{statusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Phase</Label>
                <Select value={form.phase} onValueChange={v => handleFormChange('phase', v)}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{phaseOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-[#6B7280] uppercase mb-2 block">Progress: {form.progress}%</Label>
              <Slider
                value={[form.progress]}
                onValueChange={([v]) => handleFormChange('progress', v)}
                max={100}
                step={5}
                className="w-full"
              />
            </div>
            
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Priority</Label>
              <div className="flex gap-2 mt-1">
                {priorityOptions.map(p => (
                  <button
                    key={p}
                    onClick={() => handleFormChange('priority', p)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${form.priority === p ? 'bg-[#3A8C8C]/10 border-[#3A8C8C] text-[#3A8C8C]' : 'border-[#D4D0C8] text-[#6B7280] hover:border-[#3A8C8C]'}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${priorityDots[p]}`} />
                    {p}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea
                value={form.notes}
                onChange={e => handleFormChange('notes', e.target.value)}
                placeholder="Add any remarks..."
                className="mt-1 w-full h-16 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none"
              />
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-[#E8E6E1]">
              {editingItem ? (
                <button onClick={handleDelete} className="text-xs text-[#C4727F] hover:underline">
                  <Trash2 size={12} className="inline mr-1" /> Delete Item
                </button>
              ) : <div />}
              <div className="flex gap-2">
                <button onClick={() => setShowItemModal(false)} className="px-4 py-2 rounded-lg border border-[#D4D0C8] text-xs text-[#6B7280] hover:bg-[#FAFAF8] transition-colors">
                  Cancel
                </button>
                <button onClick={handleSave} data-testid="save-timeframe-btn" className="px-4 py-2 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
                  Save
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}
